import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Plus,
  DollarSign,
  Pencil,
  Trash2,
  Check,
  X,
  Package,
  Calendar,
  Save,
  FileText,
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import {
  UPSERT_SOFTWARE_CONFIG,
  ADD_SOFTWARE_SKU,
  UPDATE_SOFTWARE_SKU,
  REMOVE_SOFTWARE_SKU,
  type OrganizationSoftwareConfig,
  type OrganizationSoftwareSku,
} from '@/graphql/organizationPricing';
import type { Product } from '@/graphql/products';

interface SoftwareTabProps {
  clientId: string;
  softwareConfig: OrganizationSoftwareConfig | null;
  softwareSkus: OrganizationSoftwareSku[];
  availableProducts: Product[];
  onRefetch: () => void;
}

export function SoftwareTab({
  clientId,
  softwareConfig,
  softwareSkus,
  availableProducts,
  onRefetch,
}: SoftwareTabProps) {
  // SSA Config state
  const [ssaEffective, setSsaEffective] = useState(softwareConfig?.ssaEffectiveDate || '');
  const [ssaExpiration, setSsaExpiration] = useState(softwareConfig?.ssaExpirationDate || '');
  const [ssaNotes, setSsaNotes] = useState(softwareConfig?.notes || '');
  const [ssaDirty, setSsaDirty] = useState(false);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState('');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [addOverridePrice, setAddOverridePrice] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);

  // Mutations
  const [upsertConfig, { loading: configSaving }] = useMutation(UPSERT_SOFTWARE_CONFIG);
  const [addSku, { loading: addingsku }] = useMutation(ADD_SOFTWARE_SKU);
  const [updateSku] = useMutation(UPDATE_SOFTWARE_SKU);
  const [removeSku] = useMutation(REMOVE_SOFTWARE_SKU);

  // Filter out products already assigned
  const assignedProductIds = new Set(softwareSkus.map(s => s.productId));
  const unassignedProducts = availableProducts.filter(p => !assignedProductIds.has(p.id));

  // SSA date handlers
  const handleSsaFieldChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setter(e.target.value);
    setSsaDirty(true);
  };

  const handleSaveSsaConfig = async () => {
    try {
      await upsertConfig({
        variables: {
          input: {
            organizationId: clientId,
            ssaEffectiveDate: ssaEffective || null,
            ssaExpirationDate: ssaExpiration || null,
            notes: ssaNotes || null,
          },
        },
      });
      setSsaDirty(false);
      onRefetch();
    } catch (error) {
      console.error('Failed to save SSA config:', error);
    }
  };

  // SKU editing handlers
  const startEditing = (sku: OrganizationSoftwareSku) => {
    setEditingId(sku.id);
    setEditingPrice((sku.overridePrice ?? sku.product.unitPrice).toString());
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingPrice('');
  };

  const savePrice = async (skuId: string) => {
    const newPrice = parseFloat(editingPrice.replace(/[^0-9.]/g, ''));
    if (!isNaN(newPrice) && newPrice >= 0) {
      try {
        await updateSku({
          variables: {
            id: skuId,
            input: { overridePrice: newPrice },
          },
        });
        onRefetch();
      } catch (error) {
        console.error('Failed to update price:', error);
      }
    }
    setEditingId(null);
    setEditingPrice('');
  };

  const resetToStandard = async (skuId: string) => {
    try {
      await updateSku({
        variables: {
          id: skuId,
          input: { overridePrice: null },
        },
      });
      onRefetch();
    } catch (error) {
      console.error('Failed to reset price:', error);
    }
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent, skuId: string) => {
    if (e.key === 'Enter') {
      savePrice(skuId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Add SKU handler
  const handleAddSku = async () => {
    if (!selectedProductId) return;

    try {
      const overridePrice = addOverridePrice
        ? parseFloat(addOverridePrice)
        : null;

      await addSku({
        variables: {
          input: {
            organizationId: clientId,
            productId: selectedProductId,
            overridePrice: !isNaN(overridePrice as number) ? overridePrice : null,
            notes: addNotes || undefined,
          },
        },
      });
      setShowAddModal(false);
      setSelectedProductId('');
      setAddOverridePrice('');
      setAddNotes('');
      onRefetch();
    } catch (error) {
      console.error('Failed to add SKU:', error);
    }
  };

  // Remove SKU handler
  const handleRemoveSku = async (skuId: string) => {
    try {
      await removeSku({ variables: { id: skuId } });
      setShowRemoveConfirm(null);
      onRefetch();
    } catch (error) {
      console.error('Failed to remove SKU:', error);
    }
  };

  // Check SSA status
  const now = new Date();
  const ssaActive = ssaEffective && ssaExpiration
    ? new Date(ssaEffective) <= now && new Date(ssaExpiration) >= now
    : false;

  return (
    <div className="space-y-6">
      {/* SSA Agreement Card */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Software Services Agreement (SSA)
            {ssaActive && (
              <span className="badge-secondary text-xs bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua">
                Active
              </span>
            )}
          </h3>
          {ssaDirty && (
            <button
              onClick={handleSaveSsaConfig}
              disabled={configSaving}
              className="btn-primary btn-sm"
            >
              <Save className="w-4 h-4" />
              {configSaving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                Effective Date
              </label>
              <input
                type="date"
                value={ssaEffective}
                onChange={handleSsaFieldChange(setSsaEffective)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                Expiration Date
              </label>
              <input
                type="date"
                value={ssaExpiration}
                onChange={handleSsaFieldChange(setSsaExpiration)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Notes
              </label>
              <input
                type="text"
                value={ssaNotes}
                onChange={handleSsaFieldChange(setSsaNotes)}
                placeholder="Agreement notes..."
                className="input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contracted Software SKUs */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <Package className="w-5 h-5" />
            Contracted Software SKUs
            <span className="badge-secondary text-xs">{softwareSkus.length}</span>
          </h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary btn-sm"
          >
            <Plus className="w-4 h-4" />
            Add SKU
          </button>
        </div>

        {softwareSkus.length === 0 ? (
          <div className="card-body text-center text-chalkboard dark:text-rain-cloud py-8">
            No software SKUs assigned to this client. Click &quot;Add SKU&quot; to assign products.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th className="text-right">Standard Price</th>
                  <th className="text-right">Client Price</th>
                  <th className="w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {softwareSkus.map((sku) => {
                  const hasOverride = sku.overridePrice !== null;
                  const displayPrice = sku.overridePrice ?? sku.product.unitPrice;
                  const discount = hasOverride && sku.product.unitPrice > 0
                    ? ((sku.product.unitPrice - sku.overridePrice!) / sku.product.unitPrice * 100).toFixed(0)
                    : null;

                  return (
                    <tr key={sku.id} className="group">
                      <td>
                        <span className="font-mono text-xs text-chalkboard dark:text-rain-cloud">
                          {sku.product.sku}
                        </span>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium text-slate dark:text-mist">{sku.product.name}</p>
                          {sku.product.description && (
                            <p className="text-xs text-chalkboard dark:text-rain-cloud">
                              {sku.product.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="text-right">
                        <span className={cn(
                          'text-sm',
                          hasOverride
                            ? 'text-chalkboard dark:text-rain-cloud line-through'
                            : 'text-slate dark:text-mist font-medium'
                        )}>
                          {formatCurrency(sku.product.unitPrice)}
                        </span>
                      </td>
                      <td className="text-right">
                        {editingId === sku.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <div className="relative w-28">
                              <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                              <input
                                type="text"
                                value={editingPrice}
                                onChange={(e) => setEditingPrice(e.target.value)}
                                onKeyDown={(e) => handlePriceKeyDown(e, sku.id)}
                                className="input pl-7 py-1 text-right text-sm"
                                autoFocus
                              />
                            </div>
                            <button
                              onClick={() => savePrice(sku.id)}
                              className="btn-ghost btn-icon btn-sm text-turquoise dark:text-aqua"
                              title="Save"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <span className={cn(
                              'font-medium',
                              hasOverride
                                ? 'text-turquoise dark:text-aqua'
                                : 'text-slate dark:text-mist'
                            )}>
                              {formatCurrency(displayPrice)}
                            </span>
                            {discount && (
                              <span className="badge-secondary text-xs">-{discount}%</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {editingId !== sku.id && (
                          <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditing(sku)}
                              className="btn-ghost btn-icon btn-sm"
                              title="Edit client price"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {hasOverride && (
                              <button
                                onClick={() => resetToStandard(sku.id)}
                                className="btn-ghost btn-icon btn-sm text-chalkboard dark:text-rain-cloud"
                                title="Reset to standard"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => setShowRemoveConfirm(sku.id)}
                              className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                              title="Remove SKU"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Software SKU Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedProductId('');
          setAddOverridePrice('');
          setAddNotes('');
        }}
        title="Add Software SKU"
      >
        <div className="space-y-4">
          {availableProducts.length === 0 ? (
            <>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                No software products found in the catalog. Add SOFTWARE category products on the{' '}
                <span className="font-semibold text-pond dark:text-pond-light">Products</span> page first,
                then return here to assign them to this client.
              </p>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </>
          ) : unassignedProducts.length === 0 ? (
            <>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                All software products in the catalog are already assigned to this client.
              </p>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                  Software Product
                </label>
                <select
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="select"
                >
                  <option value="">Select a product...</option>
                  {unassignedProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.sku} — {product.name} ({formatCurrency(product.unitPrice)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                  Override Price (optional)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                  <input
                    type="text"
                    value={addOverridePrice}
                    onChange={(e) => setAddOverridePrice(e.target.value)}
                    placeholder="Leave blank for standard price"
                    className="input pl-8"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  placeholder="Notes about this SKU..."
                  className="input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedProductId('');
                    setAddOverridePrice('');
                    setAddNotes('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSku}
                  className="btn-primary"
                  disabled={!selectedProductId || addingsku}
                >
                  <Plus className="w-4 h-4" />
                  {addingsku ? 'Adding...' : 'Add SKU'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={!!showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(null)}
        title="Remove Software SKU"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to remove this software SKU from the client&apos;s contract?
            This will also remove any price override history.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowRemoveConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => showRemoveConfirm && handleRemoveSku(showRemoveConfirm)}
              className="btn-primary bg-rust hover:bg-rust/90"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
